import { SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_WHEEL_PRICE_SETTING } from "constants/data";
import { useMemo, useState } from "react";
import { getUserType } from "util/auth";
import { USER_TYPE_LIST } from "constants/user-type";
import { PRICE_SETTING_TIRE } from "../../proc_fee/types/tire";
import { WheelPriceEditable, WheelPriceSettingState } from "../types/wheel-price";

interface EditableNumericCellProps {
  value: number | string | null;
  sx: SxProps<Theme>;
  onBlur: (e: React.FocusEvent<HTMLTableCellElement>) => void;
  onInput: (e: React.FormEvent<HTMLTableCellElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => void;
}

/**
 * Editable contentEditable cell whose content is imperatively synced with `value` via a ref.
 * React does not manage the inner text nodes, so user-driven DOM changes (typing/deleting)
 * do not desynchronize with the React fiber tree.
 */
function EditableNumericCell({ value, sx, onBlur, onInput, onKeyDown }: EditableNumericCellProps) {
  // Restrict to a numeric string so dangerouslySetInnerHTML cannot inject markup.
  const safeHtml = value === "" || value == null ? "" : String(Number(value) || 0);

  return (
    <TableCell
      sx={sx}
      contentEditable
      suppressContentEditableWarning
      onBlur={onBlur}
      onInput={onInput}
      onKeyDown={onKeyDown}
      // We let React own the cell content via dangerouslySetInnerHTML rather than `children`,
      // so the inner text nodes are not added to the React fiber tree (children reconciliation
      // is skipped entirely). React still updates the DOM when `safeHtml` changes between
      // renders, and during typing the state-derived `safeHtml` stays stable, so user input
      // is preserved.
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}

/**
 * WheelPriceTable component renders a table with wheel price settings.
 * @param wheelPriceTable - The list of wheel price settings.
 * @function onUpdatePrice - The function to update the wheel price settings.
 * @returns The WheelPriceTable component.
 */
export default function WheelPriceTable({
  wheelPriceTable,
  onUpdatePrice,
  storePriceSetting,
}: WheelPriceSettingState) {
  const ZERO_WIDTH_SPACE = "\u200B";
  const [error, setError] = useState<Record<string, boolean>>({});
  const shouldHideProfitColumn = useMemo(
    () => storePriceSetting === PRICE_SETTING_TIRE.SELLING,
    [storePriceSetting],
  );
  const headerData = useMemo(() => {
    if (!shouldHideProfitColumn) return HEADER_WHEEL_PRICE_SETTING;
    return HEADER_WHEEL_PRICE_SETTING.filter((header) => header.label !== "粗利率（％）");
  }, [shouldHideProfitColumn]);

  const validateNumber = (
    index: number,
    value: number | string,
    column: keyof WheelPriceEditable,
  ) => {
    if (String(value).length > 9) {
      setError((prev) => ({
        ...prev,
        [`${column}-${index}`]: true,
      }));
      return;
    }
    setError((prev) => ({
      ...prev,
      [`${column}-${index}`]: false,
    }));
  };

  const handleUpdate = (
    e: React.FormEvent<HTMLTableCellElement>,
    index: number,
    column: keyof WheelPriceEditable,
  ) => {
    if (error[`${column}-${index}`]) return;
    const target = e.target as HTMLTableCellElement;
    const isNumericField = column === "fixed_discount" || column === "sale_price_rate_cost";
    // For numeric fields strip every non-digit (including ZWS, line breaks the browser
    // inserts in empty contentEditable cells, etc.) so that a truly empty user input is
    // detected as "" and isCleared branches reliably.
    const sanitizedText = isNumericField
      ? target.innerText.replace(/[^0-9]/g, "")
      : target.innerText.replaceAll(ZERO_WIDTH_SPACE, "");

    if (!isNumericField && !sanitizedText.length) {
      e.currentTarget.innerText = "0";
    }
    onUpdatePrice(
      index,
      column,
      isNumericField ? sanitizedText : sanitizedText || "0",
      (originalValue) => {
        e.currentTarget.innerText = originalValue;
      },
    );
  };
  const handleInput = (e: any, index: number, column: keyof WheelPriceEditable) => {
    const sanitizedValue = e.target.innerText.replace(/[^0-9]/g, "");
    validateNumber(index, sanitizedValue || 0, column);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    const { key, ctrlKey, metaKey } = e;
    if (
      [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
        "Enter",
        "Escape",
      ].includes(key)
    ) {
      return;
    }
    if (ctrlKey || metaKey) return;
    if (!/^[0-9]$/.test(key)) {
      e.preventDefault();
    }
  };

  return (
    <TableContainer
      component={Table}
      sx={{
        boxShadow: "none",
        width: "100%",
        minWidth: "1100px",
        borderCollapse: "unset",
        borderSpacing: "2px 1px",
      }}
    >
      <TableHead>
        <TableRowComponent data={headerData} type="header" />
      </TableHead>
      <TableBody>
        {wheelPriceTable.map((price, index) => (
          <TableRow key={price.brand_cd}>
            <TableCellComponent
              sx={{
                backgroundColor: "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
              }}
            >
              {price.brand_cd}
            </TableCellComponent>

            <TableCellComponent
              sx={{
                backgroundColor:
                  price.titleEditable === "true" ? "var(--white)" : "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "regular",
                textAlign: "left",
              }}
              contentEditable={price.titleEditable}
              suppressContentEditableWarning
              onBlur={(e) => handleUpdate(e, index, "maker_name")}
            >
              {price.maker_name}
            </TableCellComponent>

            <TableCellComponent
              sx={{
                backgroundColor:
                  price.titleEditable === "true" ? "var(--white)" : "var(--offwhite-400)",
                border: "2px solid var(--silver-150)",
                fontWeight: "regular",
                textAlign: "left",
              }}
              contentEditable={price.titleEditable}
              suppressContentEditableWarning
              onBlur={(e) => handleUpdate(e, index, "brand_name")}
            >
              {price.brand_name}
            </TableCellComponent>

            <EditableNumericCell
              value={price.fixed_discount as number | string | null}
              sx={{
                width: "212px",
                maxWidth: "212px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                backgroundColor: "var(--white)",
                border: "2px solid var(--silver-150)",
                borderColor: error[`fixed_discount-${index}`]
                  ? "var(--red-800)"
                  : "var(--silver-150)",
                fontWeight: "regular",
                textAlign: "center",
                height: "48px",
                fontSize: { xs: "16px", md: "18px", xl: "20px" },
                boxSizing: "border-box",
                lineHeight: "normal",
                py: "2px",
                color:
                  (getUserType() === USER_TYPE_LIST.store.value || getUserType() === "C0006") &&
                  price.fixed_discount_store_login_edit_flag === 1
                    ? "var(--red-800)"
                    : "var(--black)",
              }}
              onBlur={(e) => handleUpdate(e, index, "fixed_discount")}
              onInput={(e) => handleInput(e, index, "fixed_discount")}
              onKeyDown={handleKeyDown}
            />

            {!shouldHideProfitColumn && (
              <EditableNumericCell
                value={price.sale_price_rate_cost as number | string | null}
                sx={{
                  width: "212px",
                  maxWidth: "212px",
                  whiteSpace: "nowrap",
                  backgroundColor: "var(--white)",
                  border: "2px solid var(--silver-150)",
                  borderColor: error[`sale_price_rate_cost-${index}`]
                    ? "var(--red-800)"
                    : "var(--silver-150)",
                  textAlign: "center",
                  height: "48px",
                  fontSize: { xs: "16px", md: "18px", xl: "20px" },
                  boxSizing: "border-box",
                  lineHeight: "normal",
                  py: "2px",
                  overflow: "hidden",
                  color:
                    (getUserType() === USER_TYPE_LIST.store.value || getUserType() === "C0006") &&
                    price.sale_price_rate_cost_store_login_edit_flag === 1
                      ? "var(--red-800)"
                      : "var(--black)",
                }}
                onBlur={(e) => handleUpdate(e, index, "sale_price_rate_cost")}
                onInput={(e) => handleInput(e, index, "sale_price_rate_cost")}
                onKeyDown={handleKeyDown}
              />
            )}
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
}
